import classnames from 'classnames'
import React, { useCallback, useState, useEffect } from 'react'
import {
  IProps,
  ISortOption,
  IWebinarCategoryRow,
  ISortedWebinarItem,
  ISortedWebinarItems,
} from './index'
import styles from './styles.scss'
import { mq } from '../../utils'
import { Sorter } from './sorter'
import { Webinar } from 'marketing-site/src/components/bespoke/webinars-list/webinar'
import { IWebinar } from 'marketing-site/src/components/bespoke/webinars'
import { sortWebinarsByDate } from 'marketing-site/src/library/utils/sortWebinarsByDate'
import { Text } from '../../elements/Text'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import { IWebinarItem } from 'marketing-site/src/components/bespoke/webinars-list'

export const WebinarSeries = ({ webinars }: IProps) => {
  const WebinarSeriesClassNames = classnames('webinar-series')
  const { translate } = useTranslate()

  const [webinarItems, setWebinarItems] = useState<IWebinarItem[]>()

  const [sortOptions, setSortOptions] = useState<ISortOption[]>([
    { name: translate('Upcoming'), value: 'upcoming', selected: true },
    { name: translate('Topic'), value: 'topic', selected: false },
    { name: translate('Region'), value: 'region', selected: false },
  ])

  const handleToggleSortOptions = useCallback(
    (value) => {
      setSortOptions(
        sortOptions.map((option) => {
          if (option.value === value) {
            return { ...option, selected: !option.selected }
          } else {
            return { ...option, selected: false }
          }
        }),
      )
      setWebinarItems(
        webinars.map((webinar: IWebinar) => ({
          id: webinar.id,
          webinar: webinar,
          expanded: false,
        })),
      )
    },
    [sortOptions],
  )

  const sortWebinars = () => {
    const activeSortOption = sortOptions.filter((filter) => filter.selected)[0]
    const activeSortOptionValue = activeSortOption.value

    switch (activeSortOptionValue) {
      case 'upcoming':
        return defaultWebinars()
      case 'topic':
        let sortedWebinarsByTopic = sortWebinarsByOption('categories')

        return sortedWebinarsByTopic.map(
          (row, index) => row.webinarItems.length && webinarRow(row, index),
        )
      case 'region':
        sortedWebinarsByTopic = sortWebinarsByOption('regions')

        return sortedWebinarsByTopic.map(
          (row, index) => row.webinarItems.length && webinarRow(row, index),
        )
      default:
        return defaultWebinars()
    }
  }

  const defaultWebinars = () => {
    const sortedWebinarItems = sortWebinarsByDate(webinarItems || [])

    return sortedWebinarItems.map((webinarItem) => (
      <div className="webinar-series__webinar-container" key={webinarItem.id}>
        <Webinar webinarItem={webinarItem} handleClick={handleToggleWebinarState} />
        <style jsx>
          {`
            .webinar-series__webinar-container {
              display: flex;
              grid-column-start: auto;
              grid-column-end: span 12;
            }

            @media (${mq.tablet}) {
              .webinar-series__webinar-container {
                grid-column-end: span 4;
              }
            }
          `}
        </style>
      </div>
    ))
  }

  const rowDescription = (rowTitle: string) => {
    const title = rowTitle.toLowerCase()
    switch (title) {
      case 'asia-pacific':
        return translate(`Live webinars on the Asia-Pacific time zones`)
      case 'north america':
        return translate(`Live webinars on the North American time zones`)
      case 'europe':
        return translate(`Live webinars on the European time zones`)
      case 'latin america':
        return translate(`Live webinars on the Latin American time zones`)
      case 'overview':
        return translate(`Deliver personal customer support at any scale`)
      case 'most watched':
        return translate(`Lorem ipsum dolor sit amet`)
      case 'automation and bots':
        return translate(`Lorem ipsum dolor sit amet`)
      case 'conversational support':
        return translate(`Deliver personal customer support at any scale`)
      case 'conversational marketing':
        return translate(`Onboard, activate, and re-engage customers`)
      case 'conversational customer engagement':
        return translate(`Onboard, activate, and re-engage customers`)
      default:
        return null
    }
  }

  const webinarRow = (rowData: IWebinarCategoryRow, index: number) => {
    const sortedWebinarItems = sortWebinarsByDate(rowData.webinarItems)
    return (
      <div className="webinar-series__webinars__row" key={index}>
        <div className="webinar-series__webinars__row__overview">
          <div className="webinar-series__webinars__row__title">
            <Text brand="default" size="heading-4">
              {rowData.name}
            </Text>
          </div>
          <div className="webinar-series__webinars__row__description">
            <Text brand="default" size="body">
              {rowDescription(rowData.name)}
            </Text>
          </div>
        </div>
        <div className="webinar-series__webinars__row__webinars">
          {sortedWebinarItems.map((webinarItem, index) => (
            <div className="webinar-series__webinar-container" key={index}>
              <Webinar webinarItem={webinarItem} handleClick={handleToggleWebinarState} />
            </div>
          ))}
        </div>
        <style jsx>
          {`
            .webinar-series__webinars__row {
              grid-column-start: 1;
              grid-column-end: span 12;
              margin-bottom: 2rem;
            }

            .webinar-series__webinars__row:last-child {
              margin-bottom: 0;
            }

            .webinar-series__webinars__row__overview {
              margin-bottom: 2rem;
            }

            .webinar-series__webinars__row__description {
              margin-top: 0.75rem;
            }

            .webinar-series__webinars__row__webinars .webinar-series__webinar-container {
              grid-column-start: auto;
              grid-column-end: span 12;
              margin-bottom: 1.25rem;
            }

            .webinar-series__webinars__row__webinars .webinar-series__webinar-container:last-child {
              margin-bottom: 0;
            }

            @media (${mq.tablet}) {
              .webinar-series__webinars__row {
                display: grid;
                grid-template-columns: [left-column] 4fr [right-column] 8fr;
                grid-column-gap: 20px;
                grid-row-gap: 60px;
              }

              .webinar-series__webinars__row__overview {
                grid-column: left-column;
                margin-bottom: 0;
              }

              .webinar-series__webinars__row__description {
                margin-top: 1rem;
              }

              .webinar-series__webinars__row__webinars {
                display: grid;
                grid-column: right-column;
                grid-template-columns: repeat(8, 1fr);
                grid-column-gap: 20px;
                grid-row-gap: 20px;
              }

              .webinar-series__webinars__row__webinars .webinar-series__webinar-container {
                display: flex;
                grid-column-end: span 4;
                margin-bottom: 0;
              }
            }
          `}
        </style>
      </div>
    )
  }

  const sortWebinarsByOption = (field: string) => {
    const category = field
    const categoryOptions: string[] = []

    webinarItems?.forEach((webinarItem) => {
      const property = webinarItem.webinar[category as keyof typeof webinarItem.webinar]

      if (Array.isArray(property)) {
        property.forEach((option) => {
          if (typeof option === 'string') {
            if (categoryOptions.includes(option)) {
              return
            } else {
              categoryOptions.push(option)
            }
          }
        })
      }
    })

    const sortedWebinars: ISortedWebinarItems = categoryOptions.map((option) => ({
      name: option,
      webinarItems: [],
    }))
    categoryOptions.forEach((option) => {
      sortedWebinars.forEach((category: ISortedWebinarItem) => {
        if (category.name === option) {
          category.webinarItems = createSortedWebinarRow(field, option)
        }
      })
    })

    return sortedWebinars
  }

  const createSortedWebinarRow = (field: string, option: string) => {
    const webinarCategory: IWebinarItem[] = []

    webinarItems?.forEach((webinarItem) => {
      const property = webinarItem.webinar[field as keyof typeof webinarItem.webinar]
      if (Array.isArray(property)) {
        if (property.some((item) => item === option)) {
          webinarCategory.push(webinarItem)
        }
      }
    })

    return webinarCategory
  }

  useEffect(() => {
    setWebinarItems(
      webinars.map((webinar: IWebinar) => ({
        id: webinar.id,
        webinar: webinar,
        expanded: false,
      })),
    )
  }, webinars)

  const handleToggleWebinarState = (webinarId: string) => {
    setWebinarItems(
      webinarItems?.map((webinarItem) => {
        if (webinarItem.id === webinarId) {
          return { ...webinarItem, expanded: !webinarItem.expanded }
        } else {
          return webinarItem
        }
      }),
    )
  }

  return (
    <div className={WebinarSeriesClassNames}>
      <div className="webinar-series__wrapper">
        <div className="webinar-series__inner">
          <div className="webinar-series__sorter-container">
            <Sorter sortOptions={sortOptions} handleToggleSortOptions={handleToggleSortOptions} />
          </div>
          <div className="webinar-series__webinars">{sortWebinars()}</div>
        </div>
      </div>
      <style jsx>{styles}</style>
    </div>
  )
}
